/**
 * Data-access layer for `public.platform_security_events`.
 *
 * RLS on the table grants SELECT to active super-admins — no edge function
 * needed (see usePlatformLoginFailureCount.js for the same pattern).
 *
 * Two query modes:
 *   1. Cursor-paginated feed  (listPlatformSecurityEventsByCursor)
 *   2. Full fetch for export  (fetchAllPlatformSecurityEvents, cap 10 000)
 *
 * Mirrors src/services/auditEvents.js's listAuditEventsByCursor + fetchAllAuditEvents.
 */
import { supabase } from '../supabase';

// ── Search ────────────────────────────────────────────────────────────────────

/**
 * Human-readable payload keys included in full-text search.
 * Mirrors SEARCH_PAYLOAD_KEYS in auditEventSearchFields.js but scoped to the
 * platform_security_events payload schema.
 *
 * Comma is the PostgREST .or() separator — no key may contain a comma.
 */
const PLATFORM_SEARCH_PAYLOAD_KEYS = [
  'company_name',
  'tenant_name',
  'removed_name',
  'deleted_name',
  'full_name',
  'member_name',
  'old_owner_name',
  'new_owner_name',
  'email',
  'removed_email',
  'deleted_email',
  'default_admin_email',
  'member_email',
];

const PLATFORM_SEARCH_COLUMNS = ['event_type'];

/**
 * Build the PostgREST .or() filter string for platform_security_events search.
 *
 * @param {string} rawSearch  Raw (un-trimmed) search string from the user.
 * @returns {string} PostgREST or-filter string, or '' if input is blank.
 */
export function buildPlatformAuditSearchOr(rawSearch) {
  const trimmed = (rawSearch || '').trim();
  if (!trimmed) return '';

  // Escape % and , which are special in PostgREST ilike / .or() syntax.
  const escaped = trimmed.replace(/[%,]/g, '\\$&');

  const columnClauses = PLATFORM_SEARCH_COLUMNS.map(
    (col) => `${col}.ilike.%${escaped}%`,
  );
  const payloadClauses = PLATFORM_SEARCH_PAYLOAD_KEYS.map(
    (key) => `payload->>${key}.ilike.%${escaped}%`,
  );

  return [...columnClauses, ...payloadClauses].join(',');
}

// ── Row columns selected on every query ──────────────────────────────────────

const SELECT_COLS = [
  'id',
  'event_type',
  'event_category',
  'actor_user_id',
  'target_user_id',
  'target_tenant_id',
  'payload',
  'created_at',
].join(', ');

// ── Cursor-paginated feed ─────────────────────────────────────────────────────

/**
 * Count rows matching the same filter shape as listPlatformSecurityEventsByCursor.
 * Uses `head: true` so the response carries only the count, not row data.
 *
 * Mirrors countAuditEvents in auditEvents.js.
 *
 * @param {object} opts  Same filter shape as listPlatformSecurityEventsByCursor.
 * @returns {Promise<{ count: number, error: Error | null }>}
 */
export async function countPlatformSecurityEvents({
  category = null,
  categories = null,
  eventType = null,
  tenantId = null,
  targetUserId = null,
  search = '',
  fromIso = null,
  toIso = null,
} = {}) {
  let q = supabase
    .from('platform_security_events')
    .select('*', { count: 'exact', head: true });

  // `categories` (array) wins over single `category` when provided and non-empty
  if (categories && categories.length > 0) {
    q = q.in('event_category', categories);
  } else if (category) {
    q = q.eq('event_category', category);
  }
  // Accept single value OR array for eventType (an event-type GROUP token
  // resolved upstream via accountEventGroups.js's resolveAccountEventFilter).
  // An EMPTY array means "match nothing" — `.in('event_type', [])` does exactly
  // that. Falling through to `else if (eventType)` would drop the filter and
  // return everything (fail open); the personal service agrees with this.
  if (Array.isArray(eventType)) q = q.in('event_type', eventType);
  else if (eventType)           q = q.eq('event_type', eventType);
  if (tenantId)  q = q.eq('target_tenant_id', tenantId);
  // Personal Account → Activity: things done TO the signed-in user — target
  // ONLY, mirroring the tenant Account → Activity semantics (entity_id = me).
  // Every emitter that belongs in a personal feed sets target_user_id = the
  // affected user, so this loses nothing versus the old actor-OR-target scope
  // while excluding admin actions the signed-in user merely PERFORMED on
  // someone else.
  if (targetUserId) {
    q = q.eq('target_user_id', targetUserId);
  }
  if (fromIso)   q = q.gte('created_at', fromIso);
  if (toIso)     q = q.lte('created_at', toIso);

  const searchOr = buildPlatformAuditSearchOr(search);
  if (searchOr) q = q.or(searchOr);

  const { count, error } = await q;
  return { count: count ?? 0, error: error || null };
}

/**
 * Cursor-paginated read for the Platform Activity feed.
 *
 * Compound cursor on (created_at, id) is the same tiebreaker approach
 * used in listAuditEventsByCursor — handles the rare two-events-at-same-
 * microsecond case without silently skipping rows at page boundaries.
 *
 * @param {object} opts
 * @param {string|null}  opts.category   - One of the 4 category values, or null for all.
 * @param {string|string[]|null} opts.eventType - Exact event_type value, an array (group), or null for all.
 * @param {string|null}  opts.tenantId   - Filter by target_tenant_id uuid, or null.
 * @param {string|null}  opts.targetUserId - Filter to rows where target_user_id = this user (personal feed), or null.
 * @param {string}       opts.search     - Free-text search (un-trimmed; sanitized here).
 * @param {string|null}  opts.fromIso    - Lower bound ISO 8601 timestamp (inclusive).
 * @param {string|null}  opts.toIso      - Upper bound ISO 8601 timestamp (inclusive).
 * @param {number}       opts.limit      - Page size (default 50).
 * @param {object|null}  opts.cursor     - { createdAt: string, id: string } from previous page.
 * @param {string}       opts.sortOrder  - 'desc' (default, newest first) | 'asc' (oldest first).
 *
 * @returns {Promise<{ data: Array, nextCursor: { createdAt, id } | null, error: Error | null }>}
 */
export async function listPlatformSecurityEventsByCursor({
  category = null,
  categories = null,
  eventType = null,
  tenantId = null,
  targetUserId = null,
  search = '',
  fromIso = null,
  toIso = null,
  limit = 50,
  cursor = null,
  sortOrder = 'desc',
} = {}) {
  const ascending = sortOrder === 'asc';

  let q = supabase
    .from('platform_security_events')
    .select(SELECT_COLS)
    .order('created_at', { ascending })
    .order('id', { ascending })
    // Fetch limit+1 so we can detect whether a next page exists without a
    // separate count query. The +1 row is sliced off before returning.
    .limit(limit + 1);

  // `categories` (array) wins over single `category` when provided and non-empty
  if (categories && categories.length > 0) {
    q = q.in('event_category', categories);
  } else if (category) {
    q = q.eq('event_category', category);
  }
  // Accept single value OR array for eventType (an event-type GROUP token
  // resolved upstream via accountEventGroups.js's resolveAccountEventFilter).
  // An EMPTY array means "match nothing" — `.in('event_type', [])` does exactly
  // that. Falling through to `else if (eventType)` would drop the filter and
  // return everything (fail open); the personal service agrees with this.
  if (Array.isArray(eventType)) q = q.in('event_type', eventType);
  else if (eventType)           q = q.eq('event_type', eventType);
  if (tenantId)  q = q.eq('target_tenant_id', tenantId);
  // Personal Account → Activity: things done TO the signed-in user — target
  // ONLY, mirroring the tenant Account → Activity semantics (entity_id = me).
  // See the matching comment in countPlatformSecurityEvents above.
  if (targetUserId) {
    q = q.eq('target_user_id', targetUserId);
  }
  if (fromIso)   q = q.gte('created_at', fromIso);
  if (toIso)     q = q.lte('created_at', toIso);

  const searchOr = buildPlatformAuditSearchOr(search);
  if (searchOr) q = q.or(searchOr);

  if (cursor?.createdAt && cursor?.id) {
    // Cursor comparison flips with sort order: descending → "less than cursor",
    // ascending → "greater than cursor". Compound tiebreaker matches order-by.
    if (ascending) {
      q = q.or(
        `created_at.gt.${cursor.createdAt},and(created_at.eq.${cursor.createdAt},id.gt.${cursor.id})`,
      );
    } else {
      q = q.or(
        `created_at.lt.${cursor.createdAt},and(created_at.eq.${cursor.createdAt},id.lt.${cursor.id})`,
      );
    }
  }

  const { data, error } = await q;
  if (error) return { data: [], nextCursor: null, error };

  const rows = data || [];
  const hasMore = rows.length > limit;
  const page = hasMore ? rows.slice(0, limit) : rows;
  const last = page[page.length - 1];
  const nextCursor = hasMore && last
    ? { createdAt: last.created_at, id: last.id }
    : null;

  return { data: page, nextCursor, error: null };
}

// ── Full fetch for export ─────────────────────────────────────────────────────

/**
 * Paginated full fetch for the export flow. Loops listPlatformSecurityEventsByCursor
 * until exhausted or the safety cap is reached. Returns whether the cap was hit
 * so the caller can warn the user.
 *
 * Capped at 10,000 rows — anything beyond is realistically a server-side job.
 * Mirrors fetchAllAuditEvents in auditEvents.js.
 *
 * @param {object} filters  Same shape as listPlatformSecurityEventsByCursor (minus cursor/limit).
 * @param {number} [maxRows=10000]
 * @returns {Promise<{ data: Array, truncated: boolean, error: Error | null }>}
 */
export async function fetchAllPlatformSecurityEvents(filters = {}, maxRows = 10000) {
  const PAGE = 200;
  const out = [];
  let cursor = null;

  while (out.length < maxRows) {
    const remaining = maxRows - out.length;
    const limit = Math.min(PAGE, remaining);
    // eslint-disable-next-line no-await-in-loop
    const { data, nextCursor, error } = await listPlatformSecurityEventsByCursor({
      ...filters,
      limit,
      cursor,
    });
    if (error) return { data: out, truncated: false, error };
    if (!data || data.length === 0) break;
    out.push(...data);
    if (!nextCursor) break;
    cursor = nextCursor;
  }

  return { data: out, truncated: out.length >= maxRows, error: null };
}
